import React, { useEffect, useState } from 'react';
import { FileText, Loader2, Trash2 } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const RECLASSIFY_OPTIONS = [
  { value: 'photo',              label: 'Photograph' },
  { value: 'site_plan',          label: 'Survey plan' },
  { value: 'site_note',          label: 'Site note' },
  { value: 'research',           label: 'Research' },
  { value: 'technical_doc',      label: 'Technical doc' },
  { value: 'client_doc',         label: 'Client doc' },
  { value: 'assessment_result',  label: 'Assessment result' },
  { value: 'unknown',            label: 'Unclassified' },
];

const formatSize = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtCapture = (iso, tz) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const s = d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  return tz ? `${s} ${tz}` : s;
};

const CAPTURE_UNAVAILABLE = 'Original capture time unavailable';
const CAPTURE_KINDS = new Set(['photo', 'site_plan', 'site_note']);

export const FileTile = ({ file, onDelete, onReclassify, fetchThumb, accent, border }) => {
  const [thumbUrl, setThumbUrl] = useState(null);
  const [thumbFailed, setThumbFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let blobUrl = null;
    if (file.thumbnail_path) {
      fetchThumb(file.id, 'thumbnail')
        .then((url) => {
          if (cancelled) { URL.revokeObjectURL(url); return; }
          blobUrl = url;
          setThumbUrl(url);
        })
        .catch(() => { if (!cancelled) setThumbFailed(true); });
    }
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [file.id, file.thumbnail_path, fetchThumb]);

  const hasImage = thumbUrl && !thumbFailed;

  return (
    <div
      data-testid={`file-tile-${file.reference}`}
      className={`group relative rounded-lg overflow-hidden bg-slate-900 border ${border} hover:border-emerald-500/60 transition`}
    >
      <div className="aspect-square bg-slate-950 flex items-center justify-center overflow-hidden">
        {hasImage ? (
          <img src={thumbUrl} alt={file.original_filename} className="w-full h-full object-cover" />
        ) : file.thumbnail_path && !thumbFailed ? (
          <Loader2 className="w-5 h-5 animate-spin text-slate-600" />
        ) : (
          <FileText className={`w-10 h-10 ${accent} opacity-60`} />
        )}
      </div>
      <div className="p-2">
        <div className="flex items-center justify-between mb-0.5">
          <span className={`text-[10px] font-mono font-semibold ${accent}`}>{file.reference}</span>
          <span className="text-[10px] text-slate-500">{formatSize(file.size_bytes)}</span>
        </div>
        <div className="text-[11px] text-slate-300 truncate" title={file.original_filename}>
          {file.original_filename}
        </div>
        {CAPTURE_KINDS.has(file.kind) && (
          (() => {
            const cap = fmtCapture(file.capture_datetime, file.capture_timezone);
            return (
              <div
                className={`text-[10px] font-mono truncate mt-0.5 ${cap ? 'text-slate-500' : 'text-red-400/70 italic'}`}
                title={cap
                  ? `EXIF DateTimeOriginal${file.capture_timezone ? ' (' + file.capture_timezone + ')' : ''}`
                  : 'No EXIF DateTimeOriginal in this image'}
                data-testid={`tile-capture-${file.reference}`}
              >
                {cap || CAPTURE_UNAVAILABLE}
              </div>
            );
          })()
        )}
      </div>
      <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
        <DropdownMenu>
          <DropdownMenuTrigger
            data-testid={`reclassify-${file.reference}`}
            className="text-[10px] px-1.5 py-0.5 bg-slate-900/90 border border-slate-700 rounded hover:bg-slate-800 text-slate-200"
          >
            Type
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-slate-900 border-slate-800 text-slate-100">
            {RECLASSIFY_OPTIONS.map((o) => (
              <DropdownMenuItem
                key={o.value}
                data-testid={`reclassify-${file.reference}-${o.value}`}
                onClick={() => onReclassify(file.id, o.value)}
                className="text-xs cursor-pointer"
              >
                {o.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <button
          type="button"
          onClick={() => onDelete(file.id)}
          data-testid={`delete-${file.reference}`}
          aria-label="Delete file"
          className="p-1 bg-slate-900/90 border border-slate-700 rounded hover:bg-red-500/20 hover:border-red-500/40"
        >
          <Trash2 className="w-3 h-3 text-slate-300" />
        </button>
      </div>
    </div>
  );
};
